import { store, store$ } from '@redux/store';
import HousingProductType from 'constants/housingProductType';
import { googleAnalytics4Event } from 'features/googleAnalytics4';
import { filter, fromEvent, map, merge, distinctUntilChanged, switchMap, of, EMPTY } from 'rxjs';
import { selectPage, selectSearchTerm, onArticleSearchResultsLoaded$ } from 'features/articleSearchResultsFacets';
import { selectModal, selectFetchingModal } from 'layout/redux/selectors';
import typeahead from 'features/articleSearchBox';

const getLinkableElement = (event) => event.target.closest('[data-page-link]');
const paginationContainer = document.querySelector('[data-component="blog-article-search-results-pagination"]');
const previousButton = paginationContainer.querySelector('[data-prev-button]');
const nextButton = paginationContainer.querySelector('[data-next-button]');
const optionList = paginationContainer.querySelector('[data-option-list]');

const isBlogArticleSearchResultSection =
    (event) => !!event.target.closest('[data-component="blog-article-search-results"]');
const isBlogArticleMostPopularSection =
    (event) => !!event.target.closest('[data-component="blog-article-search-results-most-popular"]');

const getStandardCardElement = (event) => event.target.closest("[data-card]");    
const getArticleElement = (event) => event.target.closest('[data-article-card]');
const isListingForYouSection = (event) => !!event.target.closest("[data-listings-for-you-container]");
const isStandardCard = (event) => !!event.target.closest("[data-standard-card]");
const isFavoriteIcon = (event) => !!event.target.closest("[data-favorite-button]");
const isCallToAction = (event) => !!event.target.closest("[data-lead-generator-opener]");
const isCommunityStandardCard = (dataset) => dataset.type === HousingProductType.Community().getName();
const isPlanStandardCard = (dataset) => dataset.type === HousingProductType.Plan().getName();
const isSpecStandardCard = (dataset) => dataset.type === HousingProductType.Spec().getName();
const isArticleCard = (event) => !!event.target.closest("[data-article-card]");
const isBreadcrumbItemElement = (event) => event.target?.closest('[data-breadcrumb-item]');
const getNavigatorItemElement = (event) => event.target.closest('[data-navigator-item]');
const breadcrumbItems = Array.from(document.querySelectorAll('[data-breadcrumb-item]'));
const backButtonHeaderElement = document.querySelector('[data-back-button-section="header"]');
const backButtonFooterElement = document.querySelector('[data-back-button-section="footer"]');

const bodyClick$ = fromEvent(document.body, 'click');

const onWindowLoad$ = fromEvent(window, 'load');

const listingForYouSectionClick$ = bodyClick$.pipe(
    filter((event) => isListingForYouSection(event))
);

/**
 * Dispatches a Analytics Event event when a tag is clicked.
 * @param tag - is the tag clicked in popular tag section.
 */
const triggerTagClickedEvent = function(tag) {
  const customProperties = {
    page_type: 'Learning_Center',
    tag_text: tag.dataset.pillText,
    destination: tag.dataset.pillUrl,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'tag_click', customProperties }));
};

const onNavigatorItemClick$ = bodyClick$.pipe(
  map(getNavigatorItemElement),
  filter(Boolean)
);

onNavigatorItemClick$.subscribe(triggerTagClickedEvent);

/**
 * Dispatches a Google Analytics 4 event when a breadcrumb link is clicked.
 * @param breadcrumbLink - is the link element that represents a
 * breadcrumb item in a website's navigation.
 */
const triggerBreadcrumbClickEvent = (breadcrumbLink) => {
    const breadcrumbIndex = breadcrumbItems.findIndex((item) => item === breadcrumbLink);
    const customProperties = {
        breadcrumb_position: breadcrumbIndex + 1,
        destination: breadcrumbLink.href,
        link_text: breadcrumbLink.innerText,
    };

    store.dispatch(googleAnalytics4Event({ eventName: 'breadcrumb_click', customProperties }));
};

const onBreadcrumbClick$ = bodyClick$.pipe(
    map(isBreadcrumbItemElement),
    filter(Boolean)
);

onBreadcrumbClick$.subscribe(triggerBreadcrumbClickEvent);

/**
 * The function `triggerPageViewEvent` dispatches a Google Analytics 4 event with custom properties
 * indicating a search results page view.
 */
const triggerPageViewEvent = function() {
    const customProperties = {
        page_type: 'Search_Results_Page',
    };
    store.dispatch(googleAnalytics4Event({ eventName: 'page_view', customProperties}));
};

onWindowLoad$.subscribe(triggerPageViewEvent);

/**
 * Dispatches a Google Analytics 4 event when a search is done.
 * @param suggestion - is the suggestion element that represents the element clicked
 * in the typeahead.
 */
const triggerSearchExecutedEvent = (suggestion) => {
    const customProperties = {
      search_value: suggestion.dataset.title,
      page_type: 'Search_Results_Page',
      click_target: suggestion.dataset.title,
    };
  
    store.dispatch(googleAnalytics4Event({ eventName: "searchterm_events", customProperties })    );
  };
  
  typeahead
    .getSuggestionListClickedObserver()
    .subscribe(triggerSearchExecutedEvent);

  /**
   * Dispatches a Google Analytics 4 event when a
   * search box button is clicked, capturing the search query and location.
   * @param inputSearchBoxValue - The `inputSearchBoxValue` parameter is the value entered by the user
   * in the search box before clicking the search button.
   */
  const triggerSearchBoxButtonClickedEvent = (inputSearchBoxValue) => {
      const customProperties = {
        new_search_term: inputSearchBoxValue,
      };
      
      store.dispatch(googleAnalytics4Event({ eventName: 'Search_Button_Clicked', customProperties }));
  };
      
 typeahead
  .getSubmitEventObserver()
  .subscribe(triggerSearchBoxButtonClickedEvent);

/**
 * Dispatches a Google Analytics event for tracking article clicks with custom properties
 *  including the search term value.
 * @param data - The `data` parameter in the `triggerSearchResultArticlesClick` function is an object
 * that contains additional properties related to the search result article being clicked. These
 * properties could include information such as the article title, author, publication date, or any
 * other relevant data associated with the article.
 */
const triggerSearchResultArticlesClick = (data) => {
    const searchTermValue = selectSearchTerm(store.getState());
    const customProperties = {
        searchTerm: searchTermValue,
        ...data
    };

    store.dispatch(googleAnalytics4Event({ eventName: 'article_click', customProperties }));
};

/**
 * The function `getAreaInteractionType` returns the type of area clicked on a card element in a web
 * page.
 * @param element - Parameter is a reference to an HTML element that you want to extract
 * the interaction type from.
 * @returns The function `getAreaInteractionType` returns the type of area interaction based on the
 * `interactionType` attribute of the provided `element`. If the `interactionType` matches one of the
 * keys in the `areaClickedPerCardElement` object, it returns the corresponding value. Otherwise, it
 * returns 'Article Clicked' as the default value. 
 */
const getAreaInteractionType = (element) => {
    const areaClickedPerCardElement = {
        image: 'image',
        title: 'title',
        callToAction: 'CTA',
        default: 'Article Clicked',
    };
    
    const interactionType = element.dataset.interactionType;
    return areaClickedPerCardElement[interactionType] || areaClickedPerCardElement.default;
};

/* Observable stream `articleSearchResultSectionClick$` using RxJS
operators. Here's a breakdown of what each part of the code is doing: */
const articleSearchResultSectionClick$ = bodyClick$.pipe(
    filter((event) => isBlogArticleSearchResultSection(event) && isArticleCard(event)),
    map(event => ({
        articleCard: getArticleElement(event),
        interactionType: getAreaInteractionType(event.target),
    }))
);

/* Observable stream `articleClickedEvent$` that listens for click
events within a blog article search result section. When a click event is detected, it maps the
event data to extract relevant information such as the article ID, article title, and interaction
type from the clicked article card. This extracted data is then structured into an object with
properties `articleId`, `articleTitle`, and `interactionType`. This object represents the details of
the article that was clicked within the search results section. */
const articleClickedEvent$ = articleSearchResultSectionClick$.pipe(
    map(data  => ({
        articleId: data.articleCard.dataset.articleSlug,
        articleTitle: data.articleCard.dataset.articleTitle,
        interactionType: data.interactionType,
    })),
);

articleClickedEvent$.subscribe(triggerSearchResultArticlesClick);


/* ***** Back Button GA4 Events ***** */

/**
 * Dispatches a Google Analytics event when a back button
 * element is clicked, capturing relevant custom properties.
 * @param element - The `element` parameter in the `triggerBackButtonClickedEvent` function is a
 * reference to the HTML element that represents the back button that was clicked.
 */
const triggerBackButtonClickedEvent = (element) => {

    const buttonType = element.dataset.backButtonType;

    const customProperties = {
      button_type: buttonType,
      navigation_type: 'button',
      page_url: window.location.href,
      destination_url: element.href,
      button_text: element.innerText,
      page_section: element.dataset.backButtonSection,
      user_interaction: 'click',
    };

    store.dispatch(googleAnalytics4Event({ eventName: 'navigation_click', customProperties }));
};


/* Observable stream `backButtonHeaderClicked$` that listens for click
events on the `backButtonHeaderElement`. If the `backButtonHeaderElement` exists, it creates an
observable stream using `fromEvent` to listen for click events on the element. The `map` operator is
then used to extract the target element from the click event. If the `backButtonHeaderElement` does
not exist, the observable stream is set to `EMPTY`, indicating that there are no events to listen
to. */
const backButtonHeaderClicked$ = backButtonHeaderElement
  ? fromEvent(backButtonHeaderElement, 'click').pipe(
      map((event) => event.target),
    )
  : EMPTY;


/* Observable stream `backButtonFooterClicked$` that listens for click
events on the `backButtonFooterElement`. If the `backButtonFooterElement` exists, it creates an
observable stream using `fromEvent` to listen for click events on the element. The `map` operator is
then used to extract the target element from the event. If the `backButtonFooterElement` does not
exist, the observable stream is set to `EMPTY`. */
const backButtonFooterClicked$ = backButtonFooterElement
  ? fromEvent(backButtonFooterElement, 'click').pipe(
      map((event) => event.target),
    )
  : EMPTY;

merge(backButtonHeaderClicked$, backButtonFooterClicked$).subscribe(triggerBackButtonClickedEvent);

/* ***** Most Popular GA4 Card Events ***** */

/**
 * Dispatches a Google Analytics 4 event for
 * tracking popular article clicks with custom properties.
 * @param customProperties - Custom properties are additional data or information that can be passed
 * along with an event or action. In the context of the `triggerSearchResultMostPopularArticlesClick`
 * function, `customProperties` would likely include specific details related to the popular article
 * that was clicked, such as the article title, category,
 */
const triggerSearchResultMostPopularArticlesClick = (customProperties) => {
    store.dispatch(googleAnalytics4Event({ eventName: 'popular_article_click', customProperties }));
};


/* Creating an observable stream `mostPopularArticlesSectionClick$` using RxJS
operators. Where we validate if the action comes from the Most Popular Section
and creates the objects needed for the GA4 events. */
const mostPopularArticlesSectionClick$ = bodyClick$.pipe(
    filter((event) => isBlogArticleMostPopularSection(event) && isArticleCard(event)),
    map(event => ({
        articleCard: getArticleElement(event),
        interactionType: getAreaInteractionType(event.target),
    }))
);

/* Observable stream `mostPopularArticleClickedEvent$`
using RxJS operators. That builds an object to be used as `customProperties`
to be logged in GA4. */
const mostPopularArticleClickedEvent$ = mostPopularArticlesSectionClick$.pipe(
    map(data => ({
        section: 'most_popular_articles',
        articleId: data.articleCard.dataset.articleSlug,
        articleTitle: data.articleCard.dataset.articleTitle,
        interactionType: data.interactionType,
    })),
);

mostPopularArticleClickedEvent$.subscribe(triggerSearchResultMostPopularArticlesClick);

/**
 * getAreaClickedName: function to get the click target event based on the element clicked
 * @param {HTMLElement} element target element
 * @returns an string object
 */
const getAreaClickedName = function(element){
    const areaClickedPerCardElement = {
        image: 'Listings Image Clicked',
        communityName: 'Listing text link',
        planName: 'Listing text link',
        specAddress: 'Listing text link',
        favoriteButton: 'Listing favorite',
        default: 'Card Clicked',
    };
    
    const elementName = element.dataset.cardElement;
    return areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
}

/**
 * Checks if a price is valid and not equal to zero.
 * @param price - is a variable that represents the price of a listing
 */
const isPriced = (price) => price && price !== '0';

/**
 * getModalClosePaidCommunityCustomProperties: function to get the custom properties of close button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalClosePaidCommunityCustomProperties = function(data){
    const dataset = data.listingInformation;
    const destionationUrl = data.url
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.priceLow,
        max_price: dataset.priceHigh,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
        is_paid: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
* getModalClosePaidPlanCustomProperties: function to get the custom properties of close button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalClosePaidPlanCustomProperties = function(data){
    const dataset = data.listingInformation;
    const destionationUrl = data.url
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        plan_id: dataset.planId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'False',
        is_plan: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
* getModalClosePaidSpecCustomProperties: function to get the custom properties of close button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalClosePaidSpecCustomProperties = function(data){
    const dataset = data.listingInformation;
    const destionationUrl = data.url
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',        
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        spec_id: dataset.specId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'True',
        is_plan: 'False',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
 * getRecommendedSubmittedPaidCommunityCustomProperties: function to get custom properties of Recommended button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getRecommendedSubmittedPaidCommunityCustomProperties = function(modalInformation){
    const { selectedRecommendedCommunities } = modalInformation;
    selectedRecommendedCommunities.forEach((dataset) => {
        const customProperties = {
            page_type: 'Search_Result',
            click_target: 'Learn Center Listings Cards',
            button_name: 'Get More Info',
            event_type: 'TDV Events',
            currency_value: 'USD',
            value: 30,
            trustbuilder_reviews: dataset.HasBuilderReviews,
            min_price: dataset.PriceLow,
            max_price: dataset.PriceHigh,
            market_id: dataset.MarketId,
            market_name: `${dataset.MarketName}, ${dataset.StateAbbreviation}`,
            community_name: dataset.CommunityName,
            community_status: dataset.MarketingStatus || 'Normal',
            community_id: dataset.CommunityId,            
            price_reduction: dataset.PriceReductionLabel,
            is_priced: isPriced(dataset.PriceLow) || isPriced(dataset.PriceHigh) ? 'True' : 'False',
            is_paid: 'True',
            zip_code: dataset.Zip,
            builder_id: dataset.BuilderId,
            builder_name: dataset.BrandName
        };
  
        store.dispatch(googleAnalytics4Event({ eventName: 'direct_rec_lead', customProperties }));
    })
  }

  /**
 * getModalSubmittedPaidCommunityCustomProperties: function to get the custom properties of submitted button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalSubmittedPaidCommunityCustomProperties = function(data){
    const dataset = data.listingInformation;
    const destionationUrl = data.url
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        button_name: 'Download Brochure',
        event_type: 'TDV Events',
        currency_value: 'USD',
        value: 30,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.priceLow,
        max_price: dataset.priceHigh,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
        is_paid: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
* getModalSubmittedPaidPlanCustomProperties: function to get the custom properties of Submitted button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalSubmittedPaidPlanCustomProperties = function(data){
    const dataset = data.listingInformation;
    const destionationUrl = data.url
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        button_name: 'Download Brochure',
        event_type: 'TDV Events',
        currency_value: 'USD',
        value: 30,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        plan_id: dataset.planId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'False',
        is_plan: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
* getModalSubmittedPaidSpecCustomProperties: function to get the custom properties of Submitted button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getModalSubmittedPaidSpecCustomProperties = function(data){
    const dataset = data.listingInformation;
    const destionationUrl = data.url
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        button_name: 'Download Brochure',
        event_type: 'TDV Events',
        currency_value: 'USD',
        value: 30,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        spec_id: dataset.specId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'True',
        is_plan: 'False',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
 * getLeadButtonClickedPaidCommunityCustomProperties: function to get the custom properties of lead button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getLeadButtonClickedPaidCommunityCustomProperties = function(standarCommunityCard){
    const dataset = standarCommunityCard.dataset;
    const destionationUrl = dataset?.url ?? standarCommunityCard?.querySelector('a')?.href;
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        button_name: 'Download Brochure',
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.priceLow,
        max_price: dataset.priceHigh,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
        is_paid: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
* getLeadButtonClickedPaidPlanCustomProperties: function to get the custom properties of lead button click
* based on the plan listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getLeadButtonClickedPaidPlanCustomProperties = function(standarPlanCard){
    const dataset = standarPlanCard.dataset;
    const destionationUrl = dataset?.url ?? standarPlanCard?.querySelector('a')?.href;
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        button_name: 'Download Brochure',
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        plan_id: dataset.planId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'False',
        is_plan: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
* getLeadButtonClickedPaidSpecCustomProperties: function to get the custom properties of lead button click
* based on the spec listing card information
* @param {object} data object with the information of the event
* @returns custom properties object
*/
const getLeadButtonClickedPaidSpecCustomProperties = function(specStandardCard){
    const dataset = specStandardCard.dataset;
    const destionationUrl = dataset?.url ?? specStandardCard?.querySelector('a')?.href;
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        button_name: 'Download Brochure',
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        spec_id: dataset.specId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'True',
        is_plan: 'False',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };
  
    return customProperties;
  }

  /**
 * getPaidCommunityCardProperties : function to return the respective custom properties
 * @param {dataset} dataset from the standard community card element
 * @returns custom properties object
 */
const getPaidCommunityCardProperties = function (data){
    const dataset = data.card.dataset;
    const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        click_target1: data.clickTarget,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.priceLow,
        max_price: dataset.priceHigh,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
        is_paid: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };

    return customProperties;
  }

  /**
 * getPaidPlanCardProperties: function to return the respective custom properties
 * @param {dataset} dataset from the standard plan card element
 * @returns custom properties object
 */
const getPaidPlanCardProperties = function (data){

    const dataset = data.card.dataset;
    const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        click_target1: data.clickTarget,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        plan_id: dataset.planId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'False',
        is_plan: 'True',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };

    return customProperties;
}

/**
 * getPaidSpecCardProperties: function to return the respective custom properties
 * @param {dataset} dataset from the standard plan card element
 * @returns custom properties object
 */
const getPaidSpecCardProperties = function (data){

    const dataset = data.card.dataset;
    const destionationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;
  
    const customProperties = {
        page_type: 'Search_Result',
        click_target: 'Learn Center Listings Cards',
        click_target1: data.clickTarget,
        destination: destionationUrl,
        trustbuilder_reviews: dataset.hasBuilderReviews,
        min_price: dataset.price,
        market_id: dataset.marketId,
        market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
        community_name: dataset.communityName,
        community_id: dataset.communityId,
        spec_id: dataset.specId,
        home_status: dataset.homeStatus,
        position: dataset.index,
        price_reduction: dataset.priceReductionLabel,
        is_priced: isPriced(dataset.price) ? 'True' : 'False',
        is_paid: 'True',
        is_spec: 'True',
        is_plan: 'False',
        zip_code: dataset.zip,
        builder_id: dataset.builderId,
        builder_name: dataset.brandName
    };

    return customProperties;
}

/**
 * Dispatches a Segment event when listing card is clicked.
 * @param data - is the data card on the listings section.
 */
const triggerListingCardClick = (event) => {
    store.dispatch(googleAnalytics4Event({ eventName: event.name, customProperties: event.customProperties }));
};

/**
 * Dispatches a Analytics Event When the call to action is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerLeadButtonClickedEvent = function(customProperties) {
    store.dispatch(googleAnalytics4Event({ eventName: 'form_open', customProperties }));
}

/**
* Dispatches a Analytics Event When the Submitt Lead Button is clicked.
* @param customProperties - is the custom properties of the event.
*/
const triggerModalSubmittedEvent = function(customProperties) {
    store.dispatch(googleAnalytics4Event({ eventName: 'generate_lead', customProperties }));
}

/**
* Dispatches a Analytics Event When the modal is closed.
* @param customProperties - is the custom properties of the event.
*/
const triggermodalCloseEvent = function(customProperties) {
    store.dispatch(googleAnalytics4Event({ eventName: 'form_close', customProperties }));
}

// Observers to Listing For You Section Events Logic
/* Stream of click events on standard cards within a "Listing For You"
section. It filters out events that are not standard cards, do not involve clicking on a favorite
icon, and are not call-to-action events. It then maps these filtered events to an object containing
the event name "card_clicked", the area clicked on the card, and the standard card element that was
clicked. */
const cardClickEvent$ = listingForYouSectionClick$.pipe(
    filter(event => isStandardCard(event) && !isFavoriteIcon(event) && !isCallToAction(event)),
    map(event => ({
        eventName: 'card_clicked',
        clickTarget: getAreaClickedName(event.target),
        card: getStandardCardElement(event)
    }))
);

/* Observable stream `favoriteIconClickEvent$` that listens for click
events in the `listingForYouSectionClick$` stream. It filters out events where the click target is a
favorite icon, then maps those events to an object with properties `eventName` set to 'saved',
`clickTarget` set to the name of the area clicked, and `card` set to the standard card element
associated with the event. */
const favoriteIconClickEvent$ = listingForYouSectionClick$.pipe(
    filter(event => isFavoriteIcon(event)),
    map(event => ({
        eventName: 'saved',
        clickTarget: getAreaClickedName(event.target),
        card: getStandardCardElement(event)
    }))
);

const standardCardClickEvent$ = merge(cardClickEvent$, favoriteIconClickEvent$);

/* Observable stream `standardSpecCardClick$` by piping the
`standardCardClickEvent$` observable stream. It filters the data to only include events where the
card is a special standard card using the `isSpecStandardCard` function. It then maps the data to
include custom properties obtained from `getPaidSpecCardProperties` function and the event name. */
const standardSpecCardClick$ = standardCardClickEvent$.pipe(
    filter((data) => isSpecStandardCard(data.card.dataset)),
    map((data) => ({
        customProperties: getPaidSpecCardProperties(data),
        name: data.eventName
    })),
);

/* Observable stream `standardPlanCardClick$` that listens for click
events on standard cards (`standardCardClickEvent$`). When a click event is detected on a standard
card, the code filters the data to check if the card is a standard plan card using the
`isPlanStandardCard` function. If the card is a standard plan card, the code then maps the data to
extract custom properties using the `getPaidPlanCardProperties` function and assigns the event name
to the `name` property. */
const standardPlanCardClick$ = standardCardClickEvent$.pipe(
    filter((data) => isPlanStandardCard(data.card.dataset)),
    map((data) => ({
        customProperties: getPaidPlanCardProperties(data),
        name: data.eventName
    })),
);

/* Observable stream `standardCommunityCardClick$` by filtering the
`standardCardClickEvent$` stream to only include events with community standard cards. It then maps
the data from these events to include custom properties obtained from
`getPaidCommunityCardProperties` function and the event name. */
const standardCommunityCardClick$ = standardCardClickEvent$.pipe(
    filter((data) => isCommunityStandardCard(data.card.dataset)),
    map((data) => ({
        customProperties: getPaidCommunityCardProperties(data),
        name: data.eventName
    })),
);

const standardCardClickMerged$ = merge(standardSpecCardClick$, standardPlanCardClick$, standardCommunityCardClick$);
standardCardClickMerged$.subscribe(triggerListingCardClick);

// Observers to Lead Button Clicked Event Logic
const callToActionStandardCardClick$ = listingForYouSectionClick$.pipe(
    filter(isCallToAction),
    map(getStandardCardElement),
    filter(Boolean),
  );
  const callToActionSpecClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isSpecStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedPaidSpecCustomProperties)
  );
  
  const callToActionPlanClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isPlanStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedPaidPlanCustomProperties)
  );
  
  const callToActionCommunityClick$ = callToActionStandardCardClick$.pipe(
    filter((standardCard) => isCommunityStandardCard(standardCard.dataset)),
    map(getLeadButtonClickedPaidCommunityCustomProperties)
  );
  
  merge(
    callToActionSpecClick$,callToActionPlanClick$,callToActionCommunityClick$
  ).subscribe(triggerLeadButtonClickedEvent);
  
  // Observers to ModalSubmitted Event Logic
  const modalSubmittedClick$ = store$.pipe(
    filter(selectFetchingModal),
    map(selectModal),
    filter(Boolean),
    filter((modalInformation) => modalInformation.name === 'leadGenerator'),
    map((modalInformation) => ({
        listingInformation: modalInformation.listingInformation,
        url: modalInformation.url
    })),
  );
  
  const modalSubmittedSpecClick$ = modalSubmittedClick$.pipe(
    filter((data) => isSpecStandardCard(data.listingInformation)),
    map(getModalSubmittedPaidSpecCustomProperties)
  );
  
  const modalSubmittedPlanClick$ = modalSubmittedClick$.pipe(
    filter((data) => isPlanStandardCard(data.listingInformation)),
    map(getModalSubmittedPaidPlanCustomProperties)
  );
  
  const modalSubmittedCommunityClick$ = modalSubmittedClick$.pipe(
    filter((data) => isCommunityStandardCard(data.listingInformation)),
    map(getModalSubmittedPaidCommunityCustomProperties)
  );
  
  merge(
    modalSubmittedSpecClick$,modalSubmittedPlanClick$,modalSubmittedCommunityClick$
  ).subscribe(triggerModalSubmittedEvent);
  
  // Observers to ModalSubmitted Event Logic
  const recommendedSubmitted$ = store$.pipe(
    map(selectModal),
    filter(Boolean),
    filter((modalInformation) => modalInformation.previousName === 'recommendedScreen'),
    filter((modalInformation) => Boolean(modalInformation.selectedRecommendedCommunities)),
    distinctUntilChanged()
  );
  
  recommendedSubmitted$.subscribe(getRecommendedSubmittedPaidCommunityCustomProperties);
  
  const modalCloseButtonClicked$ = fromEvent(document.body, "modalClosed").pipe(
    map((event) => event?.detail?.modalInformation),
    filter(Boolean)
  );
  
  const modalCloseSpecClick$ = modalCloseButtonClicked$.pipe(
    filter((data) => isSpecStandardCard(data.listingInformation)),
    map(getModalClosePaidSpecCustomProperties)
  );
  
  const modalClosePlanClick$ = modalCloseButtonClicked$.pipe(
    filter((data) => isPlanStandardCard(data.listingInformation)),
    map(getModalClosePaidPlanCustomProperties)
  );
  
  const modalCloseCommunityClick$ = modalCloseButtonClicked$.pipe(
    filter((data) => isCommunityStandardCard(data.listingInformation)),
    map(getModalClosePaidCommunityCustomProperties)
  );
  
  merge(
    modalCloseSpecClick$,modalClosePlanClick$,modalCloseCommunityClick$
  ).subscribe(triggermodalCloseEvent);

/**
* Dispatches a Analytics Event When the pagination is clicked.
* @param customProperties - is the custom properties of the event.
*/
const triggerPaginationClickEvent = function(customProperties) {
    store.dispatch(googleAnalytics4Event({ eventName: 'pagination_click', customProperties }));
}

// observable to emmit the new page after prev button click event
const previousButton$ = fromEvent(previousButton, 'click').pipe(
    map(() => selectPage(store.getState())),
    map((page) => ({
        current_page: page-1,
        destination_page: page,
        navigation_method: 'previous',
    })),
);

// observable to emmit the new page after next button click event
const nextButton$ = fromEvent(nextButton, 'click').pipe(
    map(() => selectPage(store.getState())),
    map((page) => ({
        current_page: page+1,
        destination_page: page,
        navigation_method: 'next',
    })),
);

// observable to emmit the new page after option button click event
const optionList$ = fromEvent(optionList, 'click').pipe(
    switchMap((event) => {
        const linkableElement = getLinkableElement(event);
        if(linkableElement) {
            event.preventDefault();
        }

        const previousPage = selectPage(store.getState());
        const page = Number(linkableElement?.dataset?.pageLink || 0);
        return of({
            current_page: page,
            destination_page: previousPage,
            navigation_method: 'page_number',
        });
    })
);

merge(previousButton$, nextButton$, optionList$).subscribe(triggerPaginationClickEvent);

// function to dispatch results_per_page_changed
const triggerResultPerPageEvent = function(articlesContainer){
    const articlesResults = articlesContainer
        ? articlesContainer.querySelectorAll('[data-article-card]') : [];
    const customProperties = {
        results_per_page: articlesResults.length
    };

    store.dispatch(googleAnalytics4Event({ eventName: 'results_per_page_changed', customProperties }));
};

// subscribe to the blogArticleSearchResults$ observable stream to dispatch articles result per page
onArticleSearchResultsLoaded$.pipe(
    map((html) => document.createRange().createContextualFragment(html)),
    distinctUntilChanged()
).subscribe(triggerResultPerPageEvent);